import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Phone, ArrowRight, ShieldCheck, Clock3, MapPin, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MEDIA, SITE } from "@/lib/content";
import MagneticGlassBox from "@/components/site/MagneticGlassBox";

export default function Hero() {
  const sectionRef = useRef(null);
  const bgRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    let rafId = 0;
    let targetX = 0;
    let targetY = 0;
    let curX = 0;
    let curY = 0;

    const onMove = (e) => {
      const rect = section.getBoundingClientRect();
      targetX = (e.clientX - rect.left) / rect.width - 0.5;
      targetY = (e.clientY - rect.top) / rect.height - 0.5;
    };

    const onLeave = () => {
      targetX = 0;
      targetY = 0;
    };

    const tick = () => {
      curX += (targetX - curX) * 0.08;
      curY += (targetY - curY) * 0.08;

      if (bgRef.current) {
        bgRef.current.style.transform = `translate3d(${curX * -28}px, ${curY * -20}px, 0) scale(1.08)`;
      }
      if (contentRef.current) {
        contentRef.current.style.transform = `perspective(1200px) rotateY(${curX * 3}deg) rotateX(${curY * -2.5}deg) translate3d(${curX * 12}px, ${curY * 8}px, 0)`;
      }

      rafId = requestAnimationFrame(tick);
    };

    section.addEventListener("mousemove", onMove);
    section.addEventListener("mouseleave", onLeave);
    rafId = requestAnimationFrame(tick);

    return () => {
      section.removeEventListener("mousemove", onMove);
      section.removeEventListener("mouseleave", onLeave);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      id="top"
      data-testid="hero-section"
      className="relative min-h-[92vh] flex items-center overflow-hidden noise-overlay"
      style={{ perspective: "1400px" }}
    >
      {/* Background video — parallax layer */}
      <div className="absolute inset-0 overflow-hidden bg-slate-950">
        <div
          ref={bgRef}
          className="absolute -inset-8 will-change-transform transition-transform duration-[120ms] ease-out"
        >
          <video
            className="w-full h-full object-cover"
            poster={MEDIA.hero}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          >
            <source src="/hero.webm" type="video/webm" />
            <source src="/hero.mp4" type="video/mp4" />
          </video>
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/88 via-slate-900/72 to-sky-950/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/70 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(2,132,199,0.28),transparent_60%)]" />
      </div>

      <div
        ref={contentRef}
        className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-12 pt-32 md:pt-36 pb-16 w-full will-change-transform transition-transform duration-[120ms] ease-out"
        style={{ transformStyle: "preserve-3d" }}
      >
        <div className="max-w-3xl" style={{ transformStyle: "preserve-3d" }}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            style={{ transform: "translateZ(40px)" }}
            className="inline-block"
          >
            <MagneticGlassBox
              max={14}
              scale={1.04}
              spotlightColor="rgba(125,211,252,0.35)"
              className="group relative inline-flex items-center overflow-hidden rounded-full border border-white/20 bg-gradient-to-r from-white/15 via-white/10 to-white/5 backdrop-blur-xl shadow-[0_10px_30px_-10px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.25)]"
            >
              <span className="pointer-events-none absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-white/70 to-transparent" />
              <span className="flex items-center gap-2 pl-3 pr-3 py-1.5 relative">
                <span
                  className="grid place-items-center w-5 h-5 rounded-full bg-gradient-to-br from-sky-300 to-sky-600 shadow-[0_0_12px_rgba(56,189,248,0.8)] ring-2 ring-white/30 transition-transform duration-300 group-hover:rotate-12 group-hover:scale-110"
                  style={{ transform: "translateZ(20px)" }}
                >
                  <Sparkles className="w-3 h-3 text-white" strokeWidth={2.5} />
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white">
                  Familiengeführt
                </span>
              </span>
              <span className="w-px h-4 bg-white/25" />
              <span className="flex items-center gap-2 pl-3 pr-4 py-1.5 relative">
                <MapPin className="w-3.5 h-3.5 text-sky-200" strokeWidth={2.2} />
                <span className="text-[11px] font-medium text-white/90 tracking-wide">
                  Bayern & Baden-Württemberg
                </span>
              </span>
            </MagneticGlassBox>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            style={{ transform: "translateZ(70px)" }}
            className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight leading-[1.05] drop-shadow-[0_8px_24px_rgba(0,0,0,0.35)]"
          >
            Professionelle <br className="hidden sm:block" />
            <span className="text-sky-300">Gebäudereinigung</span> in Bayern & Baden-Württemberg.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            style={{ transform: "translateZ(30px)" }}
            className="mt-6 text-base md:text-lg text-slate-200/90 max-w-2xl leading-relaxed"
          >
            Zuverlässig, gründlich, familiengeführt. Kola Gebäudereinigung sorgt für saubere
            Räume, gepflegte Immobilien und einen starken ersten Eindruck — ob privat
            oder gewerblich.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            style={{ transform: "translateZ(60px)" }}
            className="mt-9 flex flex-wrap gap-3"
          >
            <Button
              asChild
              data-testid="hero-cta-primary"
              className="rounded-full bg-sky-500 hover:bg-sky-400 text-white px-7 h-12 text-[15px] shadow-xl shadow-sky-500/30 transition-transform hover:-translate-y-0.5"
            >
              <a href="#kontakt" className="inline-flex items-center gap-2">
                Angebot anfragen <ArrowRight className="w-4 h-4" />
              </a>
            </Button>
            <Button
              asChild
              data-testid="hero-cta-secondary"
              variant="outline"
              className="rounded-full border-white/25 bg-white/5 text-white hover:bg-white hover:text-slate-900 px-7 h-12 text-[15px] backdrop-blur"
            >
              <a href={SITE.phoneHref} className="inline-flex items-center gap-2">
                <Phone className="w-4 h-4" /> Jetzt anrufen
              </a>
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            style={{ transform: "translateZ(20px)" }}
            className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl"
          >
            {[
              { i: ShieldCheck, label: "Garantie", t: "Versichert & zuverlässig" },
              { i: Clock3, label: "Pünktlichkeit", t: "Pünktliche Termine" },
              { i: MapPin, label: "Region", t: "Bayern & Baden-Württemberg" },
            ].map(({ i: Icon, label, t }, idx) => (
              <motion.div
                key={t}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.45 + idx * 0.08 }}
              >
                <MagneticGlassBox
                  max={16}
                  scale={1.04}
                  spotlightColor="rgba(125,211,252,0.4)"
                  className="group relative overflow-hidden rounded-2xl border border-white/15 bg-gradient-to-b from-white/10 via-white/5 to-transparent backdrop-blur-xl shadow-[0_20px_50px_-20px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.2)] hover:border-sky-300/40"
                >
                  {/* top specular highlight */}
                  <span className="pointer-events-none absolute inset-x-5 top-0 h-px bg-gradient-to-r from-transparent via-white/70 to-transparent" />
                  {/* shine sweep */}
                  <span
                    className="pointer-events-none absolute -inset-1 translate-x-[-120%] group-hover:translate-x-[120%] transition-transform duration-[1400ms] ease-out"
                    style={{
                      background:
                        "linear-gradient(115deg, transparent 42%, rgba(255,255,255,0.14) 50%, transparent 58%)",
                    }}
                  />

                  <div className="relative flex items-center gap-3.5 px-4 py-3.5">
                    <span
                      className="relative grid place-items-center w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400/40 via-sky-500/25 to-sky-700/40 border border-sky-300/40 shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_6px_20px_-6px_rgba(56,189,248,0.6)] shrink-0 transition-all duration-500 group-hover:rotate-[-6deg] group-hover:scale-110"
                      style={{ transform: "translateZ(50px)" }}
                    >
                      <Icon className="w-4 h-4 text-white" strokeWidth={2.3} />
                      {/* glowing orbit dot */}
                      <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-sky-300 shadow-[0_0_10px_rgba(125,211,252,0.9)] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </span>
                    <div className="flex flex-col min-w-0" style={{ transform: "translateZ(30px)" }}>
                      <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-sky-200/90 leading-none transition-colors duration-300 group-hover:text-sky-100">
                        {label}
                      </span>
                      <span className="mt-1.5 text-[14px] font-semibold text-white tracking-tight leading-tight">
                        {t}
                      </span>
                    </div>
                  </div>

                  {/* bottom hairline */}
                  <span className="pointer-events-none absolute inset-x-5 bottom-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
                </MagneticGlassBox>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
